import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import './CompleteProfile.css';

const ROLE_OPTIONS = [
  'Actor',
  'Audience',
  'Director',
  'Writer',
  'Cameraman',
  'Photographer',
  'Editor',
  'Producer',
  'Cinematographer',
  'Sound Designer',
  'Music Composer',
  'Costume Designer',
  'Makeup Artist',
  'Production Designer',
  'Art Director',
  'VFX Artist',
  'Stunt Coordinator',
  'Casting Director',
  'Line Producer',
  'Assistant Director',
  'Gaffer',
  'Grip',
  'Other'
];

const CompleteProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
  });

  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [customRole, setCustomRole] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRoleToggle = (role: string) => {
    if (role === 'Other') {
      if (selectedRoles.includes('Other')) {
        setSelectedRoles(selectedRoles.filter(r => r !== 'Other'));
        setCustomRole('');
      } else {
        if (selectedRoles.length >= 5) {
          setError('Maximum 5 roles allowed');
          return;
        }
        setSelectedRoles([...selectedRoles, 'Other']);
      }
      return;
    }

    if (selectedRoles.includes(role)) {
      setSelectedRoles(selectedRoles.filter(r => r !== role));
    } else {
      if (selectedRoles.length >= 5) {
        setError('Maximum 5 roles allowed');
        setTimeout(() => setError(''), 3000);
        return;
      }
      setSelectedRoles([...selectedRoles, role]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name || !formData.city || !formData.state) {
      setError('Name, city, and state are required');
      return;
    }

    if (selectedRoles.length === 0) {
      setError('Please select at least one role');
      return;
    }

    if (selectedRoles.includes('Other') && !customRole.trim()) {
      setError('Please specify your custom role');
      return;
    }

    let finalRoles = [...selectedRoles];
    if (selectedRoles.includes('Other') && customRole.trim()) {
      finalRoles = selectedRoles.map(r => r === 'Other' ? customRole.trim() : r);
    }

    setLoading(true);

    try {
      await api.post('/user/complete-profile', {
        ...formData,
        roles: finalRoles
      });

      await refreshUser();
      navigate('/content');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to complete profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="complete-profile-container">
      <div className="complete-profile-card">
        <h1>Complete Your Profile</h1>
        <p className="subtitle">Please provide your details to continue</p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="profile-form">
          <div className="form-group">
            <label>Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Your full name"
              required
            />
          </div>

          {/* Roles Selection */}
          <div className="form-group">
            <label>Roles * (Select 1-5)</label>
            <p className="role-hint">Choose your primary roles in the industry</p>
            <div className="roles-grid">
              {ROLE_OPTIONS.map((role) => (
                <button
                  key={role}
                  type="button"
                  className={`role-btn ${selectedRoles.includes(role) ? 'selected' : ''}`}
                  onClick={() => handleRoleToggle(role)}
                >
                  {role}
                  {selectedRoles.includes(role) && <span className="checkmark">✓</span>}
                </button>
              ))}
            </div>

            {selectedRoles.includes('Other') && (
              <div className="custom-role-input">
                <input
                  type="text"
                  value={customRole}
                  onChange={(e) => setCustomRole(e.target.value)}
                  placeholder="Specify your role"
                  maxLength={50}
                />
              </div>
            )}

            <div className="selected-roles-display">
              <strong>Selected ({selectedRoles.length}/5):</strong>
              {selectedRoles.map((role) => (
                <span key={role} className="selected-role-tag">
                  {role === 'Other' && customRole ? customRole : role}
                  <button
                    type="button"
                    onClick={() => handleRoleToggle(role)}
                    className="remove-role-btn"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Address</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Street address"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>City *</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                placeholder="City"
                required
              />
            </div>

            <div className="form-group">
              <label>State *</label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                placeholder="State"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label>Pincode</label>
            <input
              type="text"
              value={formData.pincode}
              onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
              placeholder="Pincode"
            />
          </div>

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? 'Completing...' : 'Complete Profile'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CompleteProfilePage;
